import { createSelector, createFeatureSelector } from '@ngrx/store';
import { ConversationNodeState } from './conversation-node.reducer';
import { dropRight, last, uniqBy } from 'lodash-es';
import { ConversationNodeLink } from './types';

export const ConversationNodeKey = 'conversationNode';

export const getConversationNodeState =
  createFeatureSelector<ConversationNodeState>(ConversationNodeKey);

const normalizeNodeName = (name: string, excludeNames = ['Source', 'Target']) => {
  if (!name) {
    return '';
  } else {
    let names = name.split('.');

    if (excludeNames.includes(last(names))) {
      names = dropRight(names);
    }

    return names.join('.');
  }
};

export const getConversationNodeLoading = createSelector(
  getConversationNodeState,
  ({ loading }) => loading,
);

export const getConversationNodeColors = createSelector(
  getConversationNodeState,
  ({ colors }) => colors,
);

export const getConversationNodeFilter = createSelector(
  getConversationNodeState,
  ({ filter }) => filter,
);

export const getConversationNodeError = createSelector(
  getConversationNodeState,
  ({ apiError }) => apiError,
);

export const getConversationNodeSeriesData = createSelector(
  getConversationNodeState,
  ({ seriesData }) => seriesData,
);

export const getConversationNodeSeriesLinks = createSelector(
  getConversationNodeState,
  ({ seriesLinks }) => seriesLinks,
);

const getConversationNodeChartData = createSelector(
  getConversationNodeSeriesData,
  getConversationNodeSeriesLinks,
  (seriesData, seriesLinks) => {
    const normalizedSeriesData = seriesData.map((data) => ({
      ...data,
      name: normalizeNodeName(data.name),
    }));

    const updatedSeriesData = normalizedSeriesData
      .map((data) => {
        const exitValue = seriesLinks.reduce(
          (accum, link) =>
            link.source == data.name && link.target === 'END.sm_dummy_node.SessionClose.Target'
              ? accum + link.value
              : accum,
          0,
        );

        return {
          ...data,
          exitValue,
          entryValue: +data.value, // we want to use entryValue to for calculations instead of directly modifying value
        };
      })
      .filter((series) => series.name);

    const updatedSeriesLinks = seriesLinks
      .map((link) => ({
        ...link,
        target: normalizeNodeName(link.target),
        source: normalizeNodeName(link.source),
      }))
      .filter((link) => link.source || link.target)
      .reduce((accum, link) => {
        if (link.target === link.source) {
          const circularNodeName = `${link.source} (Circular Reference)`;
          const foundDataItem = updatedSeriesData.find((data) => data.name === link.source);
          const circularDataItem = { ...foundDataItem, name: circularNodeName };

          updatedSeriesData.push(circularDataItem);
          link.target = circularNodeName;
        }
        const isExists = accum.find(
          (currentLink) => currentLink.target === link.target && currentLink.source === link.source,
        );

        if (!isExists) {
          accum.push(link);
        }
        return accum;
      }, [] as ConversationNodeLink[])
      .filter((link) => !!link);

    return {
      seriesLinks: updatedSeriesLinks,
      seriesData: uniqBy(updatedSeriesData, 'name'),
    };
  },
);

const getConversationNodeTotals = createSelector(getConversationNodeChartData, ({ seriesData }) =>
  seriesData.reduce(
    (acc, obj) => ({
      seriesDataEntriesTotal: acc.seriesDataEntriesTotal + obj.entryValue,
      seriesDataExitsTotal: acc.seriesDataExitsTotal + obj.exitValue,
    }),
    { seriesDataEntriesTotal: 0, seriesDataExitsTotal: 0 },
  ),
);

export const getConversationNodeChartDataWithTotals = createSelector(
  getConversationNodeChartData,
  getConversationNodeTotals,
  (data, totals) => [{ ...data, ...totals }],
);

export const getFocusedConversationNodeLoading = createSelector(
  getConversationNodeState,
  ({ focusedNodeLoading }) => focusedNodeLoading,
);

export const getFocusedConversationNodeError = createSelector(
  getConversationNodeState,
  ({ focusedNodeApiError }) => focusedNodeApiError,
);

export const getFocusedConversationNodeSeriesData = createSelector(
  getConversationNodeState,
  ({ focusedNodeSeriesData }) => focusedNodeSeriesData,
);

export const getFocusedConversationNodeSeriesLinks = createSelector(
  getConversationNodeState,
  ({ focusedNodeSeriesLinks }) => focusedNodeSeriesLinks,
);

const getFocusedConversationNodeChartData = createSelector(
  getFocusedConversationNodeSeriesData,
  getFocusedConversationNodeSeriesLinks,
  getConversationNodeChartData,
  (seriesData, seriesLinks, { seriesData: nodeChartSeriesData }) => {
    const updatedSeriesData = seriesData
      .map((data) => {
        const { entryValue, exitValue } = nodeChartSeriesData.find((node) =>
          data.name.includes(node.name),
        );

        return {
          ...data,
          entryValue,
          exitValue,
        };
      })
      .filter((series) => series.name);

    return { seriesLinks, seriesData: updatedSeriesData };
  },
);

export const getFocusedConversationNodeChartDataWithTotals = createSelector(
  getFocusedConversationNodeChartData,
  getConversationNodeTotals,
  (data, totals) => [{ ...data, ...totals }],
);
